export const MINERU_API_BASE = 'https://app.gaozhuan.net/mineru-api'
export const MINERU_PARSE_API = `${MINERU_API_BASE}/parse-sync`
export const MINERU_UPLOAD_API = `${MINERU_API_BASE}/upload`
export const MINERU_TASKS_API = `${MINERU_API_BASE}/tasks`
export const MINERU_STATUS_API = `${MINERU_API_BASE}/status`

export const VIDEO_FILE_EXTS = ['mp4', 'webm', 'ogg', 'mov', 'm4v', 'avi', 'mkv']
export const VIDEO_FILE_ACCEPT = '.mp4,.webm,.ogg,.mov,.m4v,.avi,.mkv,video/*'

export const PARSEABLE_FILE_EXTS = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']
export const PARSEABLE_FILE_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.bmp,image/*'

/** 右键「生成 MD」支持的源文件类型 */
export const MD_GENERATE_FILE_EXTS = ['pdf', 'doc', 'docx']

const DEFAULT_FETCH_TIMEOUT = 60000
const DEFAULT_POLL_INTERVAL = 2500
const DEFAULT_POLL_TIMEOUT = 10 * 60 * 1000
const MAX_CONSECUTIVE_POLL_ERRORS = 5

export function getFileExtFromName(name) {
  const base = (name || '').split('?')[0]
  const fileName = base.split('/').pop() || base
  let decoded = fileName
  try {
    decoded = decodeURIComponent(fileName)
  } catch (e) {
    // keep raw
  }
  const dot = decoded.lastIndexOf('.')
  return dot >= 0 ? decoded.slice(dot + 1).toLowerCase() : ''
}

export function getFileBaseName(name) {
  const base = (name || '').split('?')[0]
  let fileName = base.split('/').pop() || base
  try {
    fileName = decodeURIComponent(fileName)
  } catch (e) {
    // keep raw
  }
  const dot = fileName.lastIndexOf('.')
  if (dot > 0) return fileName.slice(0, dot)
  return fileName || '未命名'
}

export function isParseableFileName(name) {
  return PARSEABLE_FILE_EXTS.includes(getFileExtFromName(name))
}

export function isMdGenerateFileName(name) {
  return MD_GENERATE_FILE_EXTS.includes(getFileExtFromName(name))
}

export function resolveRemoteFileUrl(url) {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  return `https://www.gaozhuan.net${url.startsWith('/') ? '' : '/'}${url}`
}

function extractErrorMessage(data, fallback) {
  if (!data) return fallback
  if (typeof data === 'string' && data.trim()) return data.trim()
  return data.message
    || data.error
    || data.detail
    || (data.result && (data.result.message || data.result.error))
    || fallback
}

function normalizeTaskStatus(raw) {
  const status = String(
    (raw && (raw.status || raw.state || raw.task_status)) || '',
  ).toLowerCase().trim()
  if (!status) return ''
  if (['done', 'success', 'ok', 'finished', 'complete'].includes(status)) return 'completed'
  if (['fail', 'failed', 'failure'].includes(status)) return 'error'
  if (['queue', 'queued', 'waiting'].includes(status)) return 'pending'
  if (['running', 'working', 'parse', 'parsing'].includes(status)) return 'processing'
  return status
}

function extractMdContent(payload) {
  if (!payload) return ''
  if (typeof payload === 'string') return payload.trim()
  const candidates = [
    payload.md_content,
    payload.markdown,
    payload.markdown_content,
    payload.content,
    payload.result && payload.result.md_content,
    payload.result && payload.result.markdown,
    payload.result && payload.result.content,
    payload.data && payload.data.md_content,
    payload.data && payload.data.markdown,
  ]
  for (let i = 0; i < candidates.length; i += 1) {
    const val = candidates[i]
    if (typeof val === 'string' && val.trim()) return val.trim()
  }
  return ''
}

async function fetchJson(url, options = {}) {
  const timeout = options.timeout != null ? options.timeout : DEFAULT_FETCH_TIMEOUT
  const externalSignal = options.signal
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  let timer = null
  const cleanup = () => {
    if (timer) clearTimeout(timer)
    if (externalSignal && controller) {
      externalSignal.removeEventListener('abort', onExternalAbort)
    }
  }
  const onExternalAbort = () => {
    if (controller) controller.abort()
  }

  if (controller) {
    if (externalSignal) {
      if (externalSignal.aborted) {
        throw new Error('任务已取消')
      }
      externalSignal.addEventListener('abort', onExternalAbort, { once: true })
    }
    if (timeout > 0) {
      timer = setTimeout(() => controller.abort(), timeout)
    }
  }

  try {
    const res = await fetch(url, {
      ...options,
      signal: controller ? controller.signal : externalSignal,
    })
    const text = await res.text()
    let data = {}
    if (text) {
      try {
        data = JSON.parse(text)
      } catch (e) {
        if (!res.ok) {
          throw new Error(text.slice(0, 200) || `请求失败(${res.status})`)
        }
        data = { raw: text }
      }
    }
    return { res, data, text }
  } catch (err) {
    if (err && err.name === 'AbortError') {
      if (externalSignal && externalSignal.aborted) {
        throw new Error('任务已取消')
      }
      throw new Error(`请求超时（${Math.round(timeout / 1000)}s）`)
    }
    throw err
  } finally {
    cleanup()
  }
}

export async function parseFileToMarkdown(file) {
  const formData = new FormData()
  formData.append('file', file)

  const { res, data } = await fetchJson(MINERU_PARSE_API, {
    method: 'POST',
    body: formData,
    timeout: 5 * 60 * 1000,
  })

  if (!res.ok || normalizeTaskStatus(data) !== 'completed') {
    throw new Error(extractErrorMessage(data, '解析失败'))
  }

  const md = extractMdContent(data)
  if (!md) throw new Error('解析结果为空')
  return md
}

export async function uploadFileToMineru(file) {
  const formData = new FormData()
  formData.append('file', file)

  const { res, data } = await fetchJson(MINERU_UPLOAD_API, {
    method: 'POST',
    body: formData,
  })

  if (!res.ok) {
    throw new Error(extractErrorMessage(data, '上传失败'))
  }

  const url = data.url
    || data.file_url
    || data.data?.url
    || data.data?.file_url
    || (typeof data.data === 'string' ? data.data : null)

  if (!url) {
    throw new Error('未获取到文件地址')
  }

  return resolveRemoteFileUrl(url)
}

export async function fetchRemoteFileAsFile(sourceUrl, options = {}) {
  const fileUrl = resolveRemoteFileUrl(sourceUrl)
  if (!fileUrl) throw new Error('源文件地址无效')

  let displayName = fileUrl.split('?')[0].split('/').pop() || 'document'
  try {
    displayName = decodeURIComponent(displayName)
  } catch (e) {
    // keep raw
  }

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timeout = options.timeout != null ? options.timeout : 120000
  let timer = null
  const externalSignal = options.signal
  const onExternalAbort = () => {
    if (controller) controller.abort()
  }

  if (controller) {
    if (externalSignal) {
      if (externalSignal.aborted) throw new Error('任务已取消')
      externalSignal.addEventListener('abort', onExternalAbort, { once: true })
    }
    timer = setTimeout(() => controller.abort(), timeout)
  }

  try {
    const fileRes = await fetch(fileUrl, {
      signal: controller ? controller.signal : externalSignal,
    })
    if (!fileRes.ok) {
      throw new Error(`无法获取原文件（HTTP ${fileRes.status}）`)
    }
    const blob = await fileRes.blob()
    if (!blob || blob.size === 0) {
      throw new Error('原文件内容为空')
    }
    return new File([blob], displayName || 'document', {
      type: blob.type || 'application/octet-stream',
    })
  } catch (err) {
    if (err && err.name === 'AbortError') {
      if (externalSignal && externalSignal.aborted) throw new Error('任务已取消')
      throw new Error('下载源文件超时')
    }
    if (err && /Failed to fetch|NetworkError|CORS/i.test(String(err.message || err))) {
      throw new Error('下载源文件失败（网络或跨域限制）')
    }
    throw err
  } finally {
    if (timer) clearTimeout(timer)
    if (externalSignal && controller) {
      externalSignal.removeEventListener('abort', onExternalAbort)
    }
  }
}

export async function parseRemoteFileToMarkdown(sourceUrl) {
  const file = await fetchRemoteFileAsFile(sourceUrl)
  return parseFileToMarkdown(file)
}

/**
 * 提交 MinerU 离线解析任务
 */
export async function submitMineruTask(file, options = {}) {
  const formData = new FormData()
  formData.append('files', file, file.name || 'document')
  formData.append('language', options.language || 'ch')
  formData.append('backend', options.backend || 'pipeline')
  formData.append('formula_enable', String(options.formula_enable !== false))
  formData.append('table_enable', String(options.table_enable !== false))
  formData.append('image_analysis', String(options.image_analysis !== false))
  formData.append('extract_images', String(options.extract_images !== false))

  const { res, data } = await fetchJson(MINERU_TASKS_API, {
    method: 'POST',
    body: formData,
    signal: options.signal,
    timeout: options.timeout != null ? options.timeout : 120000,
  })

  if (!(res.ok || res.status === 202) || !data.task_id) {
    throw new Error(extractErrorMessage(data, `提交解析任务失败（HTTP ${res.status}）`))
  }

  return {
    ...data,
    status: normalizeTaskStatus(data) || data.status || 'pending',
  }
}

/** 查询任务状态 GET /tasks/{task_id} */
export async function getMineruTask(taskId, options = {}) {
  const { res, data } = await fetchJson(
    `${MINERU_TASKS_API}/${encodeURIComponent(taskId)}`,
    { signal: options.signal, timeout: options.timeout != null ? options.timeout : 30000 },
  )
  if (!res.ok) {
    throw new Error(extractErrorMessage(data, `查询任务状态失败（HTTP ${res.status}）`))
  }
  return {
    ...data,
    status: normalizeTaskStatus(data) || data.status,
  }
}

/**
 * 简易状态/结果 GET /status/{task_id}
 */
export async function getMineruTaskStatus(taskId, options = {}) {
  const { res, data } = await fetchJson(
    `${MINERU_STATUS_API}/${encodeURIComponent(taskId)}`,
    { signal: options.signal, timeout: options.timeout != null ? options.timeout : 30000 },
  )
  if (!res.ok) {
    throw new Error(extractErrorMessage(data, `查询解析结果失败（HTTP ${res.status}）`))
  }
  return {
    ...data,
    status: normalizeTaskStatus(data) || data.status,
    md_content: extractMdContent(data),
  }
}

export function estimateMineruTaskProgress(statusInfo = {}) {
  const status = normalizeTaskStatus(statusInfo) || statusInfo.status || 'pending'
  const queued = Number(statusInfo.queued_ahead)
  if (status === 'completed') return 100
  if (status === 'error') return 100
  if (status === 'processing') return 62
  if (status === 'pending') {
    if (Number.isFinite(queued) && queued > 0) {
      return Math.max(6, Math.min(28, 28 - Math.min(queued, 10) * 2))
    }
    return 18
  }
  return 8
}

export function mineruTaskStatusLabel(status) {
  const map = {
    pending: '排队中',
    processing: '解析中',
    completed: '已完成',
    error: '失败',
    uploading: '上传中',
    saving: '保存中',
  }
  return map[status] || status || '未知'
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms)
    if (!signal) return
    const onAbort = () => {
      clearTimeout(timer)
      reject(new Error('任务已取消'))
    }
    if (signal.aborted) {
      onAbort()
      return
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

/**
 * 轮询直到 completed / error（带超时与连续失败保护）
 */
export async function pollMineruTask(taskId, options = {}) {
  const interval = options.interval || DEFAULT_POLL_INTERVAL
  const timeout = options.timeout != null ? options.timeout : DEFAULT_POLL_TIMEOUT
  const onProgress = typeof options.onProgress === 'function' ? options.onProgress : null
  const signal = options.signal
  const startedAt = Date.now()
  let consecutiveErrors = 0
  let lastInfo = null

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (signal && signal.aborted) {
      throw new Error('任务已取消')
    }
    if (Date.now() - startedAt > timeout) {
      throw new Error(`解析超时（已等待 ${Math.round(timeout / 60000)} 分钟）`)
    }

    let info
    try {
      info = await getMineruTaskStatus(taskId, { signal, timeout: 30000 })
      consecutiveErrors = 0
    } catch (statusErr) {
      try {
        info = await getMineruTask(taskId, { signal, timeout: 30000 })
        consecutiveErrors = 0
      } catch (taskErr) {
        consecutiveErrors += 1
        const msg = (taskErr && taskErr.message)
          || (statusErr && statusErr.message)
          || '查询任务失败'
        if (onProgress) {
          onProgress({
            task_id: taskId,
            status: (lastInfo && lastInfo.status) || 'processing',
            message: `状态查询异常（${consecutiveErrors}/${MAX_CONSECUTIVE_POLL_ERRORS}）：${msg}`,
            queued_ahead: lastInfo && lastInfo.queued_ahead,
          })
        }
        if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
          throw new Error(`连续 ${MAX_CONSECUTIVE_POLL_ERRORS} 次查询失败：${msg}`)
        }
        await sleep(interval, signal)
        continue
      }
    }

    const status = normalizeTaskStatus(info) || info.status || ''
    lastInfo = { ...info, status }
    if (onProgress) onProgress(lastInfo)

    if (status === 'completed') {
      let md = extractMdContent(info)
      // 任务接口完成但未带正文时，再拉一次 status 取结果
      if (!md) {
        try {
          const detail = await getMineruTaskStatus(taskId, { signal, timeout: 60000 })
          md = extractMdContent(detail)
          if (md) {
            return { ...detail, status: 'completed', md_content: md }
          }
        } catch (e) {
          // fall through
        }
      }
      if (!md) {
        throw new Error('解析已完成，但未返回 Markdown 内容')
      }
      return {
        ...info,
        status: 'completed',
        md_content: md,
      }
    }

    if (status === 'error') {
      throw new Error(extractErrorMessage(info, '解析任务失败'))
    }

    // 未知状态也继续轮询，但给出提示
    if (status && !['pending', 'processing', 'uploading', 'queued'].includes(status)) {
      if (onProgress) {
        onProgress({
          ...lastInfo,
          message: info.message || `任务状态：${status}`,
        })
      }
    }

    await sleep(interval, signal)
  }
}

/**
 * 远程文件 → 提交离线任务 → 轮询 → Markdown
 */
export async function parseRemoteFileToMarkdownViaTask(sourceUrl, options = {}) {
  if (options.onProgress) {
    options.onProgress({ status: 'uploading', message: '正在下载源文件…', phase: 'download' })
  }
  const file = await fetchRemoteFileAsFile(sourceUrl, {
    signal: options.signal,
    timeout: options.downloadTimeout != null ? options.downloadTimeout : 120000,
  })

  if (options.onProgress) {
    options.onProgress({
      status: 'uploading',
      message: `正在提交解析任务（${file.name || 'document'}）…`,
      phase: 'upload',
    })
  }

  const submitted = await submitMineruTask(file, options)
  if (!submitted.task_id) {
    throw new Error('提交成功但未返回 task_id')
  }

  if (options.onProgress) {
    options.onProgress({
      ...submitted,
      task_id: submitted.task_id,
      status: submitted.status || 'pending',
      message: submitted.message || '任务已提交，等待解析…',
      phase: 'queued',
    })
  }

  return pollMineruTask(submitted.task_id, {
    interval: options.interval,
    timeout: options.pollTimeout,
    signal: options.signal,
    onProgress: options.onProgress,
  })
}
